import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

const MIN = 1
const MAX = 28
const REPEAT_DELAY = 320
const REPEAT_INTERVAL = 90

export function MonthDayField({
  value,
  onChange,
  fieldLabel,
  decreaseLabel,
  increaseLabel,
  note,
}: {
  value: number
  onChange: (value: number) => void
  fieldLabel: string
  decreaseLabel: string
  increaseLabel: string
  note: string
}) {
  const [text, setText] = useState(String(value))
  const [shown, setShown] = useState(value)
  const holdTimeout = useRef<number | undefined>(undefined)
  const holdInterval = useRef<number | undefined>(undefined)
  const held = useRef(false)

  if (shown !== value) {
    setShown(value)
    setText(String(value))
  }

  function step(delta: number) {
    const next = Math.max(MIN, Math.min(MAX, value + delta))
    if (next !== value) onChange(next)
  }

  function holdStart(delta: number) {
    held.current = true
    step(delta)
    window.clearTimeout(holdTimeout.current)
    window.clearInterval(holdInterval.current)
    holdTimeout.current = window.setTimeout(() => {
      holdInterval.current = window.setInterval(() => step(delta), REPEAT_INTERVAL)
    }, REPEAT_DELAY)
  }

  function holdStop() {
    window.clearTimeout(holdTimeout.current)
    window.clearInterval(holdInterval.current)
  }

  useEffect(
    () => () => {
      window.clearTimeout(holdTimeout.current)
      window.clearInterval(holdInterval.current)
    },
    [],
  )

  return (
    <div>
      <div className="flex h-field items-center rounded-nested border border-hairline bg-field px-1">
        <StepButton
          label={decreaseLabel}
          disabled={value <= MIN}
          onPointerDown={() => holdStart(-1)}
          onPointerUp={holdStop}
          onPointerLeave={holdStop}
          onClick={() => {
            if (held.current) {
              held.current = false
              return
            }
            step(-1)
          }}
        >
          <MinusIcon />
        </StepButton>

        <input
          value={text}
          onChange={(event) => {
            let digits = event.target.value.replace(/\D/g, '').slice(0, 2)
            if (digits.length === 2 && Number(digits) > MAX) {
              digits = digits[1] === '0' ? String(MAX) : (digits[1] ?? '')
            }
            setText(digits)
            const parsed = Number(digits)
            if (digits && parsed >= MIN && parsed <= MAX) onChange(parsed)
          }}
          onFocus={(event) => event.target.select()}
          onBlur={() => setText(String(value))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur()
              return
            }
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
            event.preventDefault()
            step(event.key === 'ArrowUp' ? 1 : -1)
          }}
          inputMode="numeric"
          maxLength={2}
          size={2}
          aria-label={fieldLabel}
          className="tabular h-full min-w-0 flex-1 border-0 bg-transparent text-center text-wheel text-text outline-hidden"
        />

        <StepButton
          label={increaseLabel}
          disabled={value >= MAX}
          onPointerDown={() => holdStart(1)}
          onPointerUp={holdStop}
          onPointerLeave={holdStop}
          onClick={() => {
            if (held.current) {
              held.current = false
              return
            }
            step(1)
          }}
        >
          <PlusIcon />
        </StepButton>
      </div>

      <div className="mt-[10px] text-hint text-text-muted">{note}</div>
    </div>
  )
}

function StepButton({
  label,
  disabled,
  onClick,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  onPointerDown: () => void
  onPointerUp: () => void
  onPointerLeave: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
      className="pressable flex h-tap w-tap flex-none items-center justify-center rounded-full text-text-strong transition-colors duration-row disabled:opacity-disabled"
    >
      {children}
    </button>
  )
}

function MinusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden className="block">
      <path d="M3.5 8h9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden className="block">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  )
}
